import { processError, withRetry, logError, PaymentError, ERROR_TYPES } from '../errors/errorHandler'

// Configuração base do Pagar.me
const PAGARME_API_URL = 'https://api.pagar.me/core/v5'

// ⚠️ IMPORTANTE: Chaves carregadas via env ou config
// Essas chaves devem vir do backend para segurança
let PAGARME_SECRET_KEY = ''
let PAGARME_PUBLIC_KEY = ''

// Configurar chaves (chamado ao carregar settings)
export const configurePagarme = (secretKey, publicKey) => {
  PAGARME_SECRET_KEY = secretKey
  PAGARME_PUBLIC_KEY = publicKey
}

// Timeout para requisições (30 segundos)
const REQUEST_TIMEOUT = 30000

// Headers para autenticação
const getHeaders = () => {
  const encodedKey = btoa(PAGARME_SECRET_KEY + ':')

  return {
    'Content-Type': 'application/json',
    'Authorization': `Basic ${encodedKey}`,
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
}

// Função auxiliar para fazer requisições
const makeRequest = async (url, options = {}) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...getHeaders(),
        ...options.headers
      },
      mode: 'cors',
      credentials: 'omit'
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch (e) {
        errorData = {
          message: `Erro HTTP ${response.status}`,
          status: response.status
        }
      }

      if (response.status === 401) {
        throw new PaymentError(
          'Erro de autenticação com Pagar.me.',
          'authentication_failed',
          ERROR_TYPES.AUTHENTICATION,
          { status: response.status, data: errorData }
        )
      }

      const error = new Error(errorData.message || `HTTP ${response.status}`)
      error.response = {
        status: response.status,
        data: errorData,
        headers: response.headers
      }
      throw error
    }

    return await response.json()
  } catch (error) {
    clearTimeout(timeoutId)
    throw error
  }
}

// Processar pagamento com cartão de crédito
export const processCardPayment = async (paymentData) => {
  try {
    // Validações
    if (!paymentData.card.number || paymentData.card.number.replace(/\s/g, '').length < 13) {
      throw new PaymentError(
        'Número do cartão inválido',
        'invalid_card_number',
        ERROR_TYPES.VALIDATION,
        { field: 'card_number' }
      )
    }

    if (!paymentData.card.cvv || paymentData.card.cvv.length < 3) {
      throw new PaymentError(
        'CVV inválido',
        'invalid_cvv',
        ERROR_TYPES.VALIDATION,
        { field: 'cvv' }
      )
    }

    if (!paymentData.card.holderName || paymentData.card.holderName.length < 3) {
      throw new PaymentError(
        'Nome do titular inválido',
        'invalid_holder_name',
        ERROR_TYPES.VALIDATION,
        { field: 'holder_name' }
      )
    }

    return await withRetry(async () => {
      const requestBody = {
        items: [{
          amount: Math.round(paymentData.amount * 100),
          description: paymentData.description,
          quantity: paymentData.quantity || 1,
          code: paymentData.itemCode || 'TICKET'
        }],
        customer: {
          name: paymentData.customer.name,
          email: paymentData.customer.email,
          document: paymentData.customer.document.replace(/\D/g, ''),
          type: 'individual',
          phones: {
            mobile_phone: {
              country_code: '55',
              area_code: paymentData.customer.phone.substring(1, 3),
              number: paymentData.customer.phone.substring(4).replace(/\D/g, '')
            }
          }
        },
        payments: [{
          payment_method: 'credit_card',
          credit_card: {
            installments: paymentData.installments || 1,
            statement_descriptor: paymentData.statementDescriptor || 'EVENTO',
            card: {
              number: paymentData.card.number.replace(/\s/g, ''),
              holder_name: paymentData.card.holderName,
              exp_month: parseInt(paymentData.card.expMonth),
              exp_year: parseInt(paymentData.card.expYear.length === 2 ? '20' + paymentData.card.expYear : paymentData.card.expYear),
              cvv: paymentData.card.cvv,
              holder_document: paymentData.card.holderDocument.replace(/\D/g, ''),
              billing_address: {
                line_1: paymentData.billingAddress?.line1 || 'Não informado',
                zip_code: paymentData.billingAddress?.zipCode?.replace(/\D/g, '') || '00000000',
                city: paymentData.billingAddress?.city || 'Não informado',
                state: paymentData.billingAddress?.state || 'SP',
                country: 'BR'
              }
            }
          }
        }],
        metadata: {
          participant_id: paymentData.participantId,
          ticket_id: paymentData.ticketId,
          coupon_code: paymentData.couponCode
        }
      }

      return await makeRequest(`${PAGARME_API_URL}/orders`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
    }, 2, 2000)
  } catch (error) {
    const processedError = processError(error, {
      operation: 'processCardPayment',
      amount: paymentData.amount,
      installments: paymentData.installments
    })
    logError(processedError)
    throw processedError
  }
}

// Gerar PIX
export const generatePix = async (pixData) => {
  try {
    // Validações
    if (!pixData.customer.name || pixData.customer.name.length < 3) {
      throw new PaymentError(
        'Nome do cliente inválido',
        'invalid_customer_name',
        ERROR_TYPES.VALIDATION,
        { field: 'customer_name' }
      )
    }

    if (!pixData.customer.document || !validateDocument(pixData.customer.document)) {
      throw new PaymentError(
        'CPF/CNPJ inválido',
        'invalid_document',
        ERROR_TYPES.VALIDATION,
        { field: 'customer_document' }
      )
    }

    return await withRetry(async () => {
      const requestBody = {
        items: [{
          amount: Math.round(pixData.amount * 100),
          description: pixData.description,
          quantity: pixData.quantity || 1,
          code: pixData.itemCode || 'TICKET'
        }],
        customer: {
          name: pixData.customer.name,
          email: pixData.customer.email,
          document: pixData.customer.document.replace(/\D/g, ''),
          type: 'individual',
          phones: {
            mobile_phone: {
              country_code: '55',
              area_code: pixData.customer.phone.substring(1, 3),
              number: pixData.customer.phone.substring(4).replace(/\D/g, '')
            }
          }
        },
        payments: [{
          payment_method: 'pix',
          pix: {
            expires_in: pixData.expiresIn || 3600 // 1 hora padrão
          }
        }],
        metadata: {
          participant_id: pixData.participantId,
          ticket_id: pixData.ticketId,
          coupon_code: pixData.couponCode
        }
      }

      return await makeRequest(`${PAGARME_API_URL}/orders`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
    }, 3, 1000)
  } catch (error) {
    const processedError = processError(error, {
      operation: 'generatePix',
      amount: pixData.amount
    })
    logError(processedError)
    throw processedError
  }
}

// Gerar Boleto
export const generateBoleto = async (boletoData) => {
  try {
    // Validações
    if (!boletoData.billingAddress?.zipCode) {
      throw new PaymentError(
        'CEP é obrigatório para boleto',
        'missing_zip_code',
        ERROR_TYPES.VALIDATION,
        { field: 'zip_code' }
      )
    }

    return await withRetry(async () => {
      const requestBody = {
        items: [{
          amount: Math.round(boletoData.amount * 100),
          description: boletoData.description,
          quantity: boletoData.quantity || 1,
          code: boletoData.itemCode || 'TICKET'
        }],
        customer: {
          name: boletoData.customer.name,
          email: boletoData.customer.email,
          document: boletoData.customer.document.replace(/\D/g, ''),
          type: 'individual',
          phones: {
            mobile_phone: {
              country_code: '55',
              area_code: boletoData.customer.phone.substring(1, 3),
              number: boletoData.customer.phone.substring(4).replace(/\D/g, '')
            }
          },
          address: {
            line_1: boletoData.billingAddress.line1,
            zip_code: boletoData.billingAddress.zipCode.replace(/\D/g, ''),
            city: boletoData.billingAddress.city,
            state: boletoData.billingAddress.state,
            country: 'BR'
          }
        },
        payments: [{
          payment_method: 'boleto',
          boleto: {
            instructions: boletoData.instructions || 'Pagar até o vencimento',
            due_at: boletoData.dueAt || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 3 dias
          }
        }],
        metadata: {
          participant_id: boletoData.participantId,
          ticket_id: boletoData.ticketId,
          coupon_code: boletoData.couponCode
        }
      }

      return await makeRequest(`${PAGARME_API_URL}/orders`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      })
    }, 3, 1000)
  } catch (error) {
    const processedError = processError(error, {
      operation: 'generateBoleto',
      amount: boletoData.amount
    })
    logError(processedError)
    throw processedError
  }
}

// Consultar status do pagamento
export const getPaymentStatus = async (orderId) => {
  try {
    return await withRetry(async () => {
      return await makeRequest(`${PAGARME_API_URL}/orders/${orderId}`, {
        method: 'GET'
      })
    }, 5, 500)
  } catch (error) {
    const processedError = processError(error, {
      operation: 'getPaymentStatus',
      orderId
    })
    logError(processedError)
    throw processedError
  }
}

// Validar cartão de crédito (algoritmo de Luhn)
export const validateCardNumber = (cardNumber) => {
  const number = cardNumber.replace(/\s/g, '')

  if (!/^\d+$/.test(number)) return false
  if (number.length < 13 || number.length > 19) return false

  let sum = 0
  let isEven = false

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i])

    if (isEven) {
      digit *= 2
      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    isEven = !isEven
  }

  return sum % 10 === 0
}

// Detectar bandeira do cartão
export const detectCardBrand = (cardNumber) => {
  const number = cardNumber.replace(/\s/g, '')

  const patterns = {
    visa: /^4/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
    discover: /^6(?:011|5)/,
    diners: /^3[0689]/,
    jcb: /^35/,
    elo: /^(4011|4312|4389|4514|4573|4576|5041|5066|5067|627780|636297|636368)/,
    hipercard: /^(384100|384140|384160|606282|637095|637568|60(?!11))/
  }

  for (const [brand, pattern] of Object.entries(patterns)) {
    if (pattern.test(number)) {
      return brand
    }
  }

  return 'unknown'
}

// Validar documento (CPF ou CNPJ)
export const validateDocument = (doc) => {
  const clean = doc.replace(/\D/g, '')

  if (clean.length === 11) {
    return validateCPF(clean)
  }

  if (clean.length === 14) {
    return validateCNPJ(clean)
  }

  return false
}

// Validar CPF
const validateCPF = (cpf) => {
  if (/^(\d)\1{10}$/.test(cpf)) return false

  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf[i]) * (10 - i)
  }
  let remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(cpf[9])) return false

  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf[i]) * (11 - i)
  }
  remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(cpf[10])) return false

  return true
}

// Validar CNPJ
const validateCNPJ = (cnpj) => {
  if (/^(\d)\1{13}$/.test(cnpj)) return false

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  let sum = 0
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnpj[i]) * weights1[i]
  }
  let remainder = sum % 11
  const digit1 = remainder < 2 ? 0 : 11 - remainder
  if (digit1 !== parseInt(cnpj[12])) return false

  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  sum = 0
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cnpj[i]) * weights2[i]
  }
  remainder = sum % 11
  const digit2 = remainder < 2 ? 0 : 11 - remainder
  if (digit2 !== parseInt(cnpj[13])) return false

  return true
}

// Formatar valor para exibição
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

// Máscara de cartão de crédito
export const maskCardNumber = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
  const matches = v.match(/\d{4,16}/g)
  const match = (matches && matches[0]) || ''
  const parts = []

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4))
  }

  if (parts.length) {
    return parts.join(' ')
  } else {
    return value
  }
}

// Máscara de data de validade (MM/YY)
export const maskExpiryDate = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')

  if (v.length >= 2) {
    return v.substring(0, 2) + '/' + v.substring(2, 4)
  }

  return v
}

/**
 * Criar token do cartão de crédito
 * @param {Object} cardData - Dados do cartão
 * @returns {Promise<string>} Token do cartão
 */
export const createCardToken = async (cardData) => {
  try {
    const publicKey = PAGARME_PUBLIC_KEY || process.env.VUE_APP_PAGARME_PUBLIC_KEY

    if (!publicKey) {
      throw new PaymentError(
        'Chave pública do Pagar.me não configurada',
        'missing_public_key',
        ERROR_TYPES.CONFIGURATION
      )
    }

    console.log('🔑 Using public key:', publicKey)

    const requestBody = {
      type: 'card',
      card: {
        number: cardData.number.replace(/\s/g, ''),
        holder_name: cardData.holderName,
        exp_month: parseInt(cardData.expMonth),
        exp_year: parseInt(cardData.expYear),
        cvv: cardData.cvv
      }
    }

    console.log('📤 Sending tokenization request to Pagar.me...')

    // Passa a chave pública como parâmetro appId na URL (igual imersaoex-lp)
    const response = await fetch(`${PAGARME_API_URL}/tokens?appId=${publicKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    const data = await response.json()

    if (!response.ok) {
      console.error('❌ Token creation failed:', data)
      throw new PaymentError(
        data.message || 'Erro ao criar token do cartão',
        'token_creation_failed',
        ERROR_TYPES.API,
        { response: data }
      )
    }

    console.log('✅ Token created successfully:', data.id)
    return data.id // Retorna o token do cartão
  } catch (error) {
    const processedError = processError(error, {
      operation: 'createCardToken'
    })
    logError(processedError)
    throw processedError
  }
}
